<script setup lang="ts">
  // Visuals ported from cartadigital-pos PosOrderViewStatusStepper.vue; the
  // step model replicates admin's ModalPosViewOrderStatus.vue flow logic
  // (Seat: Received→Progress→Finished; Away: +ReadyToTake; Delivery:
  // +Delivering; canceled/declined → error badge + Reactivar).
  import { computed, getCurrentInstance, inject, ref, Ref } from 'vue';
  import { OrderStatus, OrderType } from '@/enums/OrderEnums';
  import { PermissionType } from '@/enums/PermissionType';
  import { IOrder } from '@/interfaces/IOrder';
  import { useAuth } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { useOrders } from '@/composition/use-orders';
  import { useUi } from '@/composition/use-ui';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';

  const order = inject<Ref<IOrder>>('order')!;
  const viewMode = inject<'interactive' | 'preview'>(
    'posV2ViewMode',
    'interactive'
  );

  const ui = useUi();
  const modals = useModal();
  const auth = useAuth();
  const ordersHelper = useOrders();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  // Legacy parity: the old status panel hides its mutating actions without
  // OrdersManage (the server rejects them anyway). Historic-only users on
  // /orders keep the visual step indicator, never the buttons.
  const canManageOrders = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );

  const isInteractive = computed(
    () => viewMode === 'interactive' && canManageOrders.value
  );

  const isCanceledOrDeclined = computed(() => {
    const s = order.value.status;
    return s === OrderStatus.Canceled || s === OrderStatus.Declined;
  });

  // Archived orders belong to a closed table: their status is frozen (the
  // server rejects changes anyway), so no prev/next/reactivate actions.
  const isArchived = computed(() => !!order.value.archived);

  interface IStep {
    labelKey: string;
    status: OrderStatus;
  }

  const steps = computed<IStep[]>(() => {
    const o = order.value;
    const result: IStep[] = [
      { labelKey: 'posV2.stepReceived', status: OrderStatus.Received },
    ];

    if (o.type !== OrderType.Seat) {
      const midStatus =
        o.type === OrderType.Delivery
          ? OrderStatus.Delivering
          : OrderStatus.ReadyToTake;
      result.push(
        { labelKey: 'posV2.stepProgress', status: OrderStatus.Progress },
        {
          labelKey:
            o.type === OrderType.Delivery
              ? 'posV2.stepDelivering'
              : 'posV2.stepReadyToTake',
          status: midStatus,
        },
        { labelKey: 'posV2.stepFinished', status: OrderStatus.Finished }
      );
    } else {
      result.push(
        { labelKey: 'posV2.stepProgress', status: OrderStatus.Progress },
        { labelKey: 'posV2.stepFinished', status: OrderStatus.Finished }
      );
    }

    return result;
  });

  const currentStepIndex = computed(() => {
    switch (order.value.status) {
      case OrderStatus.Received:
        return 0;
      case OrderStatus.Progress:
        return 1;
      case OrderStatus.Delivering:
      case OrderStatus.ReadyToTake:
        return 2;
      case OrderStatus.Finished:
        return order.value.type === OrderType.Seat ? 2 : 3;
      default:
        return 0;
    }
  });

  // At Received the footer actions own Accept/Reject, so no prev/next here
  // (same rule as the old modal's stepper).
  const prevStep = computed<IStep | null>(() => {
    if (isCanceledOrDeclined.value || isArchived.value) return null;
    if (currentStepIndex.value <= 0) return null;
    const prev = steps.value[currentStepIndex.value - 1];
    return prev.status === OrderStatus.Received ? null : prev;
  });

  const nextStep = computed<IStep | null>(() => {
    if (isCanceledOrDeclined.value || isArchived.value) return null;
    const index = currentStepIndex.value;
    if (index === 0 || index >= steps.value.length - 1) return null;
    return steps.value[index + 1];
  });

  const isCompleted = computed(
    () =>
      !isCanceledOrDeclined.value &&
      order.value.status === OrderStatus.Finished
  );

  const updating = ref(false);

  async function setStatus(status: OrderStatus) {
    if (updating.value) return;

    updating.value = true;
    try {
      const didUpdate = await ordersHelper.setOrderStatus(order.value, status);
      if (didUpdate) {
        // Best-effort snapshot refresh: history orders are usually NOT in the
        // Pinia store, so socket updates never reach the fetched instance.
        try {
          await ordersHelper.refreshOrder({ order: order.value });
        } catch (refreshError) {
          console.error('Error refreshing order', refreshError);
        }
      }
    } catch (error) {
      ui.serverError(error, i18nT('posV2.statusUpdateError'));
    } finally {
      updating.value = false;
    }
  }

  async function handleReactivate() {
    if (updating.value) return;

    const statusText = i18nT(
      order.value.status === OrderStatus.Canceled
        ? 'posV2.canceledWord'
        : 'posV2.declinedWord'
    );

    const confirmed = await modals.confirm({
      title: i18nT('posV2.reactivateOrder'),
      message: i18nT('posV2.reactivateConfirm', { status: statusText }),
    });
    if (!confirmed) return;

    await setStatus(OrderStatus.Progress);
  }
</script>

<template>
  <div class="pos-v2-view-stepper">
    <!-- Canceled / declined state -->
    <div v-if="isCanceledOrDeclined" class="canceled-state">
      <div class="canceled-main">
        <PosUiBadge color="error" variant="solid" size="lg" icon="close-circle">
          {{
            order.status === OrderStatus.Canceled
              ? $t('posV2.statusCanceled')
              : $t('posV2.statusDeclined')
          }}
        </PosUiBadge>
        <p class="canceled-text">
          {{
            order.status === OrderStatus.Canceled
              ? $t('posV2.canceledOrderText')
              : $t('posV2.declinedOrderText')
          }}
        </p>
      </div>

      <PosUiButton
        v-if="isInteractive && !isArchived"
        color="primary"
        variant="solid"
        size="lg"
        icon="undo"
        :loading="updating"
        :block="true"
        @click="handleReactivate"
      >
        {{ $t('posV2.reactivateOrder') }}
      </PosUiButton>
    </div>

    <!-- Normal flow -->
    <template v-else>
      <PosUiScrollable
        class="stepper-track-container"
        orientation="horizontal"
        variant="overlay"
        theme="page"
        size="sm"
        :hide-scrollbar="true"
        :arrows="true"
        scroll-class="stepper-track"
      >
        <template v-for="(step, index) in steps">
          <div
            v-if="index > 0"
            :key="`connector-${step.status}`"
            class="step-connector"
            :class="{
              'step-connector--completed': index <= currentStepIndex,
              'step-connector--stretch': steps.length === 2,
            }"
          ></div>

          <div
            :key="step.status"
            class="step"
            :class="{
              'step--past': index < currentStepIndex,
              'step--current': index === currentStepIndex,
            }"
          >
            <div class="step-dot">
              <PosUiIcon
                v-if="index < currentStepIndex"
                name="check"
                class="step-check"
              />
              <span v-else class="step-number">{{ index + 1 }}</span>
            </div>
            <span class="step-label">{{ $t(step.labelKey) }}</span>
          </div>
        </template>
      </PosUiScrollable>

      <div v-if="isInteractive && (prevStep || nextStep)" class="stepper-actions">
        <button
          v-if="prevStep"
          type="button"
          class="action-btn action-prev"
          :disabled="updating"
          @click="setStatus(prevStep.status)"
        >
          <PosUiIcon name="arrow-left" class="btn-icon" />
          <span class="btn-text">
            <span class="btn-title">{{ $t('posV2.stepBackTo') }}</span>
            <span class="btn-label">{{ $t(prevStep.labelKey) }}</span>
          </span>
        </button>

        <button
          v-if="nextStep"
          type="button"
          class="action-btn action-next"
          :disabled="updating"
          @click="setStatus(nextStep.status)"
        >
          <span class="btn-text">
            <span class="btn-title">{{ $t('posV2.stepMarkAs') }}</span>
            <span class="btn-label">{{ $t(nextStep.labelKey) }}</span>
          </span>
          <PosUiIcon name="arrow-right" class="btn-icon" />
        </button>
      </div>

      <div v-else-if="isInteractive && isCompleted" class="completed-state">
        <PosUiIcon name="check-circle" class="completed-icon" />
        <span class="completed-text">{{ $t('posV2.orderCompleted') }}</span>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-view-stepper {
    @apply tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;

    .canceled-state {
      @apply tw-flex tw-flex-col tw-gap-3 tw-p-3;

      .canceled-main {
        @apply tw-flex tw-items-center tw-gap-3;
      }

      .canceled-text {
        @apply tw-mb-0 tw-text-sm tw-text-neutral-600;
      }
    }

    .stepper-track-container {
      @apply tw-border-b tw-border-neutral-100;
    }

    :deep(.stepper-track) {
      @apply tw-flex tw-items-center tw-px-4 tw-py-3;
    }

    .step-connector {
      @apply tw-mx-2 tw-h-0.5 tw-w-6 tw-rounded-full tw-bg-neutral-300 tw-transition-colors tw-duration-200 sm:tw-mx-3 sm:tw-w-10;

      &--stretch {
        @apply tw-min-w-6 tw-flex-1 sm:tw-min-w-10;
      }

      &--completed {
        @apply tw-bg-emerald-500;
      }
    }

    .step {
      @apply tw-relative tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2;

      .step-dot {
        @apply tw-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-neutral-300 tw-bg-neutral-100 tw-transition-all tw-duration-200;
      }

      .step-check {
        @apply tw-text-sm tw-text-white;
      }

      .step-number {
        @apply tw-text-xs tw-font-bold tw-text-neutral-400;
      }

      .step-label {
        @apply tw-whitespace-nowrap tw-text-sm tw-text-neutral-400 tw-transition-colors tw-duration-200;
      }

      &--past {
        .step-dot {
          @apply tw-border-emerald-500 tw-bg-emerald-500;
        }

        .step-label {
          @apply tw-text-neutral-600;
        }
      }

      &--current {
        .step-dot {
          @apply tw-border-orange-600 tw-bg-orange-600 tw-ring-4 tw-ring-orange-600/25;
        }

        .step-number {
          @apply tw-text-white;
        }

        .step-label {
          @apply tw-font-semibold tw-text-neutral-900;
        }
      }
    }

    .stepper-actions {
      @apply tw-flex tw-w-full;

      .action-btn {
        @apply tw-flex tw-min-h-[52px] tw-flex-1 tw-items-center tw-justify-center tw-gap-2 tw-transition-colors;

        &:disabled {
          @apply tw-cursor-not-allowed tw-opacity-50;
        }

        .btn-icon {
          @apply tw-text-lg;
        }

        .btn-text {
          @apply tw-flex tw-flex-col tw-items-start;

          .btn-title {
            @apply tw-text-xs tw-font-normal;
            line-height: 1.1em;
          }

          .btn-label {
            @apply tw-text-sm tw-font-semibold;
            line-height: 1.2em;
          }
        }

        &.action-prev {
          @apply tw-border-r tw-border-neutral-200 tw-bg-neutral-50 tw-text-neutral-600 hover:tw-bg-neutral-100;
        }

        &.action-next {
          @apply tw-bg-orange-600 tw-text-white hover:tw-bg-orange-700;
        }
      }
    }

    .completed-state {
      @apply tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-3 tw-text-emerald-600;

      .completed-icon {
        @apply tw-text-xl;
      }

      .completed-text {
        @apply tw-text-sm tw-font-medium;
      }
    }
  }
</style>
